import { useCallback, useEffect, useRef, useState } from "react";
import { api, API, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { useOpenById } from "../lib/useOpenById";
import { ARTWORK_STATUSES, ARTWORK_STATUS_COLORS, SAMPLE_STATUS_COLORS } from "../lib/fields";
import { ListPage } from "../components/ListPage";
import { EntityForm } from "../components/EntityForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Upload, FileText, Pencil } from "lucide-react";

function ArtworkCard({ aw, onChanged, onEdit }) {
  const { t, canWrite } = useApp();
  const fileRef = useRef(null);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/files/artworks/${aw.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("save") + " ✓");
      onChanged();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const setStatus = async (status) => {
    try {
      await api.put(`/artworks/${aw.id}`, { status });
      toast.success(`${aw.artwork_number} → ${status}`);
      onChanged();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const viewPdf = () => {
    const token = localStorage.getItem("lf_token");
    window.open(`${API}/files/download/${aw.file_id}?auth=${token}`, "_blank");
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="artwork-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-mono text-xs text-gray-400">{aw.artwork_number} · v{aw.version}</p>
          <p className="font-semibold text-sm text-[#1B2A4A] leading-snug">{aw.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">{aw.account_name}</p>
        </div>
        <Badge className={`${ARTWORK_STATUS_COLORS[aw.status] || ""} hover:bg-inherit shrink-0`} data-testid="artwork-status-badge">{aw.status}</Badge>
      </div>
      <div className="flex items-center gap-2 text-xs">
        {aw.file_id ? (
          <button onClick={viewPdf} className="flex items-center gap-1 text-blue-600 hover:underline" data-testid="artwork-view-pdf-btn">
            <FileText className="h-3.5 w-3.5" />{aw.file_name || t("view_pdf")}
          </button>
        ) : (
          <span className="text-gray-400">{t("no_file")}</span>
        )}
      </div>
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-50">
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileRef.current?.click()} data-testid="artwork-upload-btn">
            <Upload className="h-3 w-3 mr-1" />{t("upload_pdf")}
          </Button>
          <Select value={aw.status} onValueChange={setStatus}>
            <SelectTrigger className="h-7 w-40 text-xs" data-testid="artwork-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{ARTWORK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <button className="text-gray-300 hover:text-gray-600 ml-auto" onClick={() => onEdit(aw)} data-testid="artwork-edit-btn">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function Samples() {
  const { t, canWrite } = useApp();
  const [artworks, setArtworks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("samples");

  const loadArtworks = useCallback(() => api.get("/artworks?limit=500").then((r) => setArtworks(r.data)).catch(() => {}), []);
  useEffect(() => { loadArtworks(); }, [loadArtworks]);
  useOpenById("artworks", (doc) => { setTab("artworks"); setEditing(doc); }, "artwork_id");

  const sampleColumns = [
    { key: "sample_number", label: t("sample_number"), render: (r) => <span className="font-mono text-xs">{r.sample_number}</span> },
    { key: "account_name", label: t("account") },
    { key: "opportunity_name", label: t("opportunities") },
    { key: "product_type", label: t("product_type"), picklist: "product_types" },
    { key: "substrate", label: t("substrate") },
    { key: "quantity", label: t("quantity"), align: "right" },
    { key: "due_date", label: t("due_date"), render: (r) => fmtDate(r.due_date) },
    { key: "status", label: t("status"), render: (r) => <Badge className={`${SAMPLE_STATUS_COLORS[r.status] || ""} hover:bg-inherit`}>{r.status}</Badge> },
    { key: "tracking_number", label: t("tracking_number"), render: (r) => <span className="font-mono text-xs">{r.tracking_number || "–"}</span> },
    { key: "owner_name", label: t("owner") },
  ];

  return (
    <div className="space-y-4" data-testid="samples-page">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="samples" data-testid="tab-samples">{t("samples_tab")}</TabsTrigger>
          <TabsTrigger value="artworks" data-testid="tab-artworks">{t("artworks")}</TabsTrigger>
        </TabsList>

        <TabsContent value="samples">
          <ListPage module="samples" titleKey="samples_tab" columns={sampleColumns} />
        </TabsContent>

        <TabsContent value="artworks" className="space-y-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("artworks")}</h1>
            {canWrite && (
              <Button data-testid="artworks-new-btn" onClick={() => setEditing({})}
                className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
                <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("new")}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="artworks-grid">
            {artworks.map((aw) => (
              <ArtworkCard key={aw.id} aw={aw} onChanged={loadArtworks} onEdit={setEditing} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? t("edit") : t("create")} – {t("artworks")}</DialogTitle></DialogHeader>
          {editing && (
            <EntityForm module="artworks" initial={editing}
              onSaved={() => { setEditing(null); loadArtworks(); }} onCancel={() => setEditing(null)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
